import React, {FC, useCallback, useEffect, useState} from 'react';
import {RouteComponentProps, Link} from '@reach/router';
import styled from 'styled-components';
import {api} from '../api/api';
import {usePageTitle} from '../hooks/usePageTitle';
import {LlmRequestLogDto, LlmSessionDetailDto, LlmSessionSummaryDto} from '../common';
import {ThemeProps} from '../styles/theme';

const Container = styled.div<ThemeProps>`
    max-width: 900px;
    margin: 0 auto;
    padding: 20px;
    color: ${props => props.theme.primaryForeground};
`;

const Header = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 20px;
`;

const Title = styled.h1`
    font-size: 1.4em;
    margin: 0;
`;

const BackLink = styled(Link)<ThemeProps>`
    color: ${props => props.theme.secondaryIcon};
    text-decoration: none;
    font-size: 0.9em;

    &:hover {
        text-decoration: underline;
    }
`;

const Message = styled.div<ThemeProps>`
    color: ${props => props.theme.secondaryIcon};
    padding: 20px 0;
`;

const SessionCard = styled.div<ThemeProps>`
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 6px;
    margin-bottom: 12px;
    overflow: hidden;
`;

const SessionHeader = styled.button<ThemeProps>`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: ${props => props.theme.primaryBackground};
    border: none;
    color: ${props => props.theme.primaryForeground};
    cursor: pointer;
    text-align: left;
    font-size: 0.95em;

    &:hover {
        background: ${props => props.theme.secondaryBackground};
    }
`;

const KindBadge = styled.span<ThemeProps>`
    font-size: 0.72em;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid ${props => props.theme.primaryBorder};
    color: ${props => props.theme.secondaryIcon};
    white-space: nowrap;
`;

const SourceText = styled.span`
    font-weight: 600;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Muted = styled.span<ThemeProps>`
    color: ${props => props.theme.secondaryIcon};
    font-size: 0.82em;
    white-space: nowrap;
`;

const Cost = styled.span<ThemeProps>`
    color: ${props => props.theme.primaryForeground};
    font-size: 0.82em;
    font-weight: 600;
    white-space: nowrap;
`;

const SessionBody = styled.div<ThemeProps>`
    border-top: 1px solid ${props => props.theme.primaryBorder};
    padding: 10px 14px;
`;

const CardLinks = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 10px;
`;

const CardChip = styled(Link)<ThemeProps>`
    font-size: 0.8em;
    color: ${props => props.theme.secondaryIcon};
    text-decoration: none;
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 4px;
    padding: 2px 8px;

    &:hover {
        text-decoration: underline;
    }
`;

const RequestRow = styled.div<ThemeProps>`
    border-top: 1px solid ${props => props.theme.primaryBorder};
    padding: 8px 0;
`;

const RequestHeader = styled.button<ThemeProps & {$error: boolean}>`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    background: none;
    border: none;
    color: ${props => props.$error ? '#d9534f' : props.theme.primaryForeground};
    cursor: pointer;
    text-align: left;
    font-size: 0.88em;
    padding: 2px 0;
`;

const StepName = styled.span`
    font-weight: 600;
    min-width: 110px;
`;

const Block = styled.div`
    margin-top: 8px;
`;

const BlockLabel = styled.div<ThemeProps>`
    font-size: 0.72em;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${props => props.theme.secondaryIcon};
    margin-bottom: 4px;
`;

const Pre = styled.pre<ThemeProps>`
    margin: 0;
    padding: 8px 10px;
    background: ${props => props.theme.secondaryBackground};
    border-radius: 4px;
    font-size: 0.82em;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 320px;
    overflow: auto;
`;

const formatTime = (time: number): string => new Date(time).toLocaleString();

const formatCost = (cost?: number): string | null => {
    if (cost === undefined) return null;
    return `$${cost < 0.01 ? cost.toFixed(4) : cost.toFixed(2)}`;
};

const formatTokens = (request: LlmRequestLogDto): string | null => {
    if (request.totalTokens === undefined) return null;
    return `${request.totalTokens} tok`;
};

const RequestView: FC<{request: LlmRequestLogDto}> = ({request}) => {
    const [expanded, setExpanded] = useState(false);
    const tokens = formatTokens(request);

    return (
        <RequestRow>
            <RequestHeader $error={request.status === 'error'} onClick={() => setExpanded(value => !value)}>
                <span>{expanded ? '▾' : '▸'}</span>
                <StepName>{request.step}</StepName>
                {request.direction !== undefined && <KindBadge>{request.direction}</KindBadge>}
                <Muted>{request.model}</Muted>
                <Muted>{request.durationMs} ms</Muted>
                {tokens !== null && <Muted>{tokens}</Muted>}
                {formatCost(request.costUsd) !== null && <Muted>{formatCost(request.costUsd)}</Muted>}
                {request.status === 'error' && <Muted>error</Muted>}
            </RequestHeader>
            {expanded && (
                <>
                    {request.messages.map((message, index) => (
                        <Block key={index}>
                            <BlockLabel>{message.role}</BlockLabel>
                            <Pre>{message.content}</Pre>
                        </Block>
                    ))}
                    {request.response !== undefined && (
                        <Block>
                            <BlockLabel>response</BlockLabel>
                            <Pre>{request.response}</Pre>
                        </Block>
                    )}
                    {request.error !== undefined && (
                        <Block>
                            <BlockLabel>error</BlockLabel>
                            <Pre>{request.error}</Pre>
                        </Block>
                    )}
                </>
            )}
        </RequestRow>
    );
};

const SessionView: FC<{session: LlmSessionSummaryDto}> = ({session}) => {
    const [expanded, setExpanded] = useState(false);
    const [detail, setDetail] = useState<LlmSessionDetailDto | null>(null);
    const [loading, setLoading] = useState(false);

    const toggle = useCallback(async () => {
        const next = !expanded;
        setExpanded(next);
        if (next && detail === null && !loading) {
            setLoading(true);
            try {
                setDetail(await api.llmLog.getSession(session.id));
            } finally {
                setLoading(false);
            }
        }
    }, [expanded, detail, loading, session.id]);

    return (
        <SessionCard>
            <SessionHeader onClick={() => { void toggle(); }}>
                <span>{expanded ? '▾' : '▸'}</span>
                <KindBadge>{session.kind}</KindBadge>
                <SourceText>{session.sourceText}</SourceText>
                <Muted>{session.requestCount} calls</Muted>
                {session.totalTokens > 0 && <Muted>{session.totalTokens} tok</Muted>}
                {formatCost(session.costUsd) !== null && <Cost>{formatCost(session.costUsd)}</Cost>}
                <Muted>{formatTime(session.createdTime)}</Muted>
            </SessionHeader>
            {expanded && (
                <SessionBody>
                    {session.cardIds.length > 0 && (
                        <CardLinks>
                            {session.cardIds.map(cardId => (
                                <CardChip key={cardId} to={`/card/${cardId}`}>card {cardId.slice(-6)}</CardChip>
                            ))}
                        </CardLinks>
                    )}
                    {loading && <Muted>Loading…</Muted>}
                    {detail?.requests.map(request => (
                        <RequestView key={request.id} request={request} />
                    ))}
                </SessionBody>
            )}
        </SessionCard>
    );
};

export const LlmLog: FC<RouteComponentProps> = () => {
    usePageTitle('LLM Log · Memkeep');
    const [sessions, setSessions] = useState<LlmSessionSummaryDto[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        api.llmLog.getSessions()
            .then(setSessions)
            .catch(loadError => setError(loadError instanceof Error ? loadError.message : 'Failed to load'));
    }, []);

    return (
        <Container>
            <Header>
                <Title>LLM Log</Title>
                <BackLink to="/">← Back</BackLink>
            </Header>
            {error !== null && <Message>{error}</Message>}
            {error === null && sessions === null && <Message>Loading…</Message>}
            {sessions !== null && sessions.length === 0 && <Message>No LLM requests logged yet.</Message>}
            {sessions?.map(session => (
                <SessionView key={session.id} session={session} />
            ))}
        </Container>
    );
};
